import React, { useState, useEffect } from 'react';
import { FiSearch, FiBell, FiChevronDown, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../../services/notificationService';

/**
 * Header Component
 * Top header bar with search, notifications, and profile
 * Matches the Sales Dashboard design
 */
const Header = ({ title = "Dashboard" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);

  // Load notifications when component mounts or when notification dropdown is opened
  useEffect(() => {
    if (user) {
      loadNotifications();
      // Refresh notifications every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      const response = await notificationService.getInAppNotifications();
      if (response.data && response.data.success) {
        setNotifications(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to debtors page with search query
      navigate('/debtors', { state: { search: searchQuery } });
      setSearchQuery('');
    }
  };

  const handleMarkAllRead = () => {
    // In a real implementation, this would call an API endpoint
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    setShowNotifications(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header className="bg-white border-b border-neutral-200 px-8 py-4 flex items-center justify-between sticky top-0 z-40">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-display font-bold text-neutral-900">
          {title}
        </h1>
      </div>

      {/* Search Bar, Language, Notification, Profile */}
      <div className="flex items-center space-x-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search here..."
            className="pl-10 pr-4 py-2 w-80 border border-neutral-200 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </form>

        {/* Settings */}
        <button
          onClick={() => navigate('/settings')}
          className="p-2 rounded-button hover:bg-neutral-100 transition-colors"
          title="Settings"
        >
          <FiSettings className="w-5 h-5 text-neutral-600" />
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-button hover:bg-neutral-100 transition-colors"
          >
            <FiBell className="w-5 h-5 text-neutral-600" />
            {/* Notification Badge */}
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-danger-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-card shadow-lg border border-neutral-200 py-2 max-h-96 overflow-y-auto">
              <div className="px-4 py-3 border-b border-neutral-200">
                <h3 className="text-sm font-semibold text-neutral-900">Notifications</h3>
              </div>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${
                      !notification.read ? 'bg-primary-50' : ''
                    }`}
                  >
                    <p className="text-sm text-neutral-900">{notification.message}</p>
                    <p className="text-xs text-neutral-500 mt-1">{notification.time}</p>
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm text-neutral-500">
                  No notifications
                </div>
              )}
              <button
                onClick={handleMarkAllRead}
                className="w-full text-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50 transition-colors"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center space-x-3 p-2 rounded-button hover:bg-neutral-100 transition-colors"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.username?.charAt(0).toUpperCase() || 'M'}
              </span>
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-neutral-900">
                {user?.businessName || user?.username || 'Merchant'}
              </div>
              <div className="text-xs text-neutral-500">
                {user?.subscription?.package === 'basic_plus' ? 'Pro' :
                 user?.subscription?.package === 'base' ? 'Base' :
                 user?.subscription?.package === 'trial' ? 'Trial' : 'Admin'}
              </div>
            </div>
            <FiChevronDown className="w-4 h-4 text-neutral-500" />
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-card shadow-lg border border-neutral-200 py-2">
              <div className="px-4 py-3 border-b border-neutral-200">
                <p className="text-sm font-semibold text-neutral-900">
                  {user?.businessName || user?.username}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  {user?.email}
                </p>
              </div>
              <button
                onClick={() => {
                  navigate('/settings');
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Settings
              </button>
              <button
                onClick={() => {
                  navigate('/select-package');
                  setShowProfileMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Subscription
              </button>
              <hr className="my-2 border-neutral-200" />
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
